import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import Modal from '@adsk/alloy-react-modal';
import { useLocale } from '@adsk/alloy-react-helpers';
import FormField from '@adsk/alloy-react-form-field';
import TextInput from '@adsk/alloy-react-text-input';
import SelectBox from '@adsk/alloy-select-box';
import { COMPONENT_VARIANTS } from '@common/constants';
import EditCompanyAddress from '@common/EditCompanyAddress';
import CompanyNameOption from './layout/CompanyNameOption';
import NameInputFooter from './layout/NameInputFooter';
import ModalFooter from './layout/ModalFooter';
import {
  companyNameStyle,
  StyledDescription,
  StyledFormField,
  StyledModal,
  StyledModalBody,
} from './styles';

const AddCompanyModal = ({
  companies,
  isOpen,
  mapBoxAccessToken,
  onAddAddressClick,
  onAddressReset,
  onCancel,
  onCloseCompanyDropdown,
  onCompanyChange,
  onCompanyDropdownStateChange,
  onCreateCompany,
  onSubmit,
  selectOnly,
  trades,
  variant,
  ...rest
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const { errors, handleBlur, handleChange, setFieldValue, values } = rest;
  const { isSaving, selectedCompany } = values;
  const companiesList = useMemo(() => {
    return selectedCompany?.id === 1
      ? [...companies, selectedCompany]
      : companies;
  }, [companies, selectedCompany]);
  const existingCompany = selectedCompany && selectedCompany?.id !== 1;
  const isProjectVariant = variant === COMPONENT_VARIANTS.PROJECT;

  return (
    <StyledModal open={isOpen} data-testid="AddCompanyModal_modal">
      <Modal.Header>
        {T.translate('TITLE')}
        <Modal.Close onClick={onCancel} />
      </Modal.Header>
      <StyledModalBody>
        <StyledFormField
          data-testid="AddCompanyModal_name_field"
          error={errors.name}
          errorVariant={FormField.ERROR_VARIANTS.TOP}
          label={T.translate('NAME_LABEL')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
          required
        >
          <SelectBox
            creatable={!selectOnly}
            data-testid="AddCompanyModal_company_selectBox"
            footerRenderer={() => {
              if (selectOnly) return <NameInputFooter />;
              return <></>;
            }}
            ignoreCase
            inputDebounce={400}
            inputValue={values.nameInput}
            invalid={errors.name}
            isLoading={false}
            isOptionDisabled={(option) => option.disabled}
            labelKey="name"
            maxMenuHeight={150}
            menuStyle={companyNameStyle}
            noResultsPlaceholder
            onChange={onCompanyChange}
            onClose={onCloseCompanyDropdown}
            onCreate={onCreateCompany}
            onInputChange={(value) => {
              if (value.length < 256) setFieldValue('nameInput', value);
            }}
            onStateChange={onCompanyDropdownStateChange}
            optionRenderer={(option) => (
              <CompanyNameOption
                addCompanyMessage={
                  isProjectVariant ? T.translate('ADD_COMPANY_NAME') : undefined
                }
                alreadySelected={
                  isProjectVariant
                    ? selectedCompany?.name === option.name
                    : undefined
                }
                disabled={option.disabled}
                name={option.name}
                nameExistsMessage={T.translate('NAME_EXISTS')}
              />
            )}
            options={companiesList}
            placeholder={
              selectOnly
                ? T.translate('NAME_SEARCH_PLACEHOLDER')
                : T.translate('NAME_ENTER_PLACEHOLDER')
            }
            searchable
            searchPlaceholder={
              selectOnly
                ? T.translate('NAME_SEARCH_PLACEHOLDER')
                : T.translate('NAME_ENTER_PLACEHOLDER')
            }
            value={selectedCompany}
            valueKey="id"
          />
        </StyledFormField>
        <StyledFormField
          label={T.translate('TRADE_LABEL')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
          data-testid="AddCompanyModal_trade_field"
        >
          <SelectBox
            id="trade"
            name="trade"
            maxMenuHeight={180}
            searchable
            clearable
            onChange={(value) => setFieldValue('trade', value)}
            options={trades}
            disabled={selectOnly || existingCompany}
            placeholder={T.translate('TRADE_PLACEHOLDER')}
            value={values.trade}
            data-testid="AddCompanyModal_trade_selectBox"
          />
        </StyledFormField>
        <StyledFormField
          data-testid="AddCompanyModal_website_field"
          error={errors.website}
          errorVariant={FormField.ERROR_VARIANTS.TOP}
          label={T.translate('WEBSITE_LABEL')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
        >
          <TextInput
            data-testid="AddCompanyModal_website_textInput"
            disabled={selectOnly || existingCompany}
            error={errors.website}
            id="website"
            name="website"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={T.translate('WEBSITE_PLACEHOLDER')}
            type="text"
            value={values.website}
          />
        </StyledFormField>
        {values.addresses.map((address, index) => (
          <EditCompanyAddress
            address={address}
            disabled={selectOnly || existingCompany}
            key="mainAddress"
            isInModal
            mapBoxAccessToken={mapBoxAccessToken}
            onAddAddressClick={onAddAddressClick}
            onResetAddress={() => onAddressReset(index)}
            setFieldValue={setFieldValue}
            statePrefix={`addresses[${index}].`}
          />
        ))}
        <StyledFormField
          data-testid="AddCompanyModal_erp_field"
          error={errors.erp}
          errorVariant={FormField.ERROR_VARIANTS.TOP}
          label={T.translate('ERP_LABEL')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
        >
          <TextInput
            data-testid="AddCompanyModal_erp_textInput"
            disabled={selectOnly || existingCompany}
            error={errors.erp}
            id="erp"
            name="erp"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={T.translate('ERP_PLACEHOLDER')}
            type="text"
            value={values.erp}
          />
        </StyledFormField>
        <StyledFormField
          data-testid="AddCompanyModal_tax_field"
          error={errors.tax}
          errorVariant={FormField.ERROR_VARIANTS.TOP}
          label={T.translate('TAX_LABEL')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
        >
          <TextInput
            error={errors.tax}
            data-testid="AddCompanyModal_tax_textInput"
            disabled={selectOnly || existingCompany}
            id="tax"
            name="tax"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={T.translate('TAX_PLACEHOLDER')}
            type="text"
            value={values.tax}
          />
        </StyledFormField>
        <StyledFormField
          data-testid="AddCompanyModal_description_field"
          error={errors.description}
          errorVariant={FormField.ERROR_VARIANTS.TOP}
          label={T.translate('DESCRIPTION_LABEL')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
        >
          <StyledDescription
            data-testid="AddCompanyModal_description_textarea"
            disabled={selectOnly || existingCompany}
            error={errors.description}
            id="description"
            minHeight="80px"
            name="description"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={T.translate('DESCRIPTION_PLACEHOLDER')}
            value={values.description}
          />
        </StyledFormField>
      </StyledModalBody>
      <Modal.Footer>
        <ModalFooter
          createDisabled={!selectedCompany}
          onCancelClick={onCancel}
          onCreateClick={onSubmit}
          isSaving={isSaving}
          showTooltip={selectOnly && isEmpty(selectedCompany)}
          submitButtonLabel={T.translate('FOOTER_ADD_BUTTON')}
          tooltipMessage={T.translate('SUBMIT_BUTTON_TOOLTIP_MESSAGE')}
        />
      </Modal.Footer>
    </StyledModal>
  );
};

AddCompanyModal.propTypes = {
  companies: PropTypes.arrayOf(PropTypes.object).isRequired,
  isOpen: PropTypes.bool.isRequired,
  mapBoxAccessToken: PropTypes.string,
  onAddAddressClick: PropTypes.func.isRequired,
  onAddressReset: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onCloseCompanyDropdown: PropTypes.func.isRequired,
  onCompanyChange: PropTypes.func.isRequired,
  onCompanyDropdownStateChange: PropTypes.func.isRequired,
  onCreateCompany: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  selectOnly: PropTypes.bool.isRequired,
  trades: PropTypes.arrayOf(PropTypes.object).isRequired,
  variant: PropTypes.oneOf(Object.values(COMPONENT_VARIANTS)).isRequired,
};

AddCompanyModal.defaultProps = {
  mapBoxAccessToken: undefined,
};

export default AddCompanyModal;
